import { createContext, useContext, useEffect, type ReactNode } from 'react'

export interface SettingsStripOverride {
  /** Appended to the section title: "Settings · Tenants · Gotham". */
  title?: string
  /** Right-edge control (a back link, a status). */
  end?: ReactNode
}

/**
 * The Settings layout owns the page strip (it spans the nav, so a section
 * cannot render its own), but a drill-down — one tenant, one organization —
 * needs to name itself there. This is the channel: a page pushes an override
 * up, the layout renders it, and the page clears it on unmount so the next
 * section does not inherit a stale name.
 */
export const SettingsStripContext = createContext<(o: SettingsStripOverride | null) => void>(() => {})

export function useSettingsStrip(override: SettingsStripOverride) {
  const set = useContext(SettingsStripContext)
  const { title, end } = override
  useEffect(() => {
    set({ title, end })
    return () => set(null)
    // `end` is a fresh element each render; keyed on what it renders instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [set, title])
}
